import { CrmNoteType } from '@plato/data/prisma';
import type { ReactElement } from 'react';
import type { CrmCustomerStatusChangeDTO } from '../../../server/dto/crm-customer-status-change-dto.ts';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { InsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import {
    type ActivityFeedRecord,
    type ActivityFeedVisitPayload,
    ActivityFeedModelType,
} from '../../../server/types/customer-activity-feed-types.ts';
import type { QuoteOrderPayload } from '../../../server/types/customer-activity.ts';
import type { TaskCompletedSource } from '../../analytics/types/analytics-events.ts';
import { ActivityFeedCustomerStatusChangeItem } from './activity-feed-customer-status-change-item.tsx';
import { ActivityFeedInsightOutcomeItem } from './activity-feed-insight-outcome-item.tsx';
import { ActivityFeedNoteItem } from './activity-feed-note-item.tsx';
import { ActivityFeedQuoteOrderItem } from './activity-feed-quote-order-item.tsx';
import { ActivityFeedTaskItem } from './activity-feed-task-item.tsx';
import { ActivityFeedVisitItem } from './activity-feed-visit-item.tsx';

type Props = {
    activity: ActivityFeedRecord;
    source: TaskCompletedSource;
};

/**
 * One past activity's card, picked by its model type.
 *
 * The payload is optional on the record and typed loosely, so this is where it is
 * narrowed: the switch already knows the model type, and a row that arrived without a
 * payload is skipped here rather than crashing the card that unwrapped it.
 */
export function ActivityFeedRecordItem({ activity, source }: Props): ReactElement | null {
    if (!activity.payload) {
        return null;
    }

    switch (activity.model_type) {
        case ActivityFeedModelType.TASK:
            return <ActivityFeedTaskItem payload={activity.payload as CrmTaskDTO} source={source} />;
        case ActivityFeedModelType.NOTE:
            return activity.payload.noteType === CrmNoteType.VISIT ? (
                <ActivityFeedVisitItem
                    payload={activity.payload as ActivityFeedVisitPayload}
                    source={source}
                />
            ) : (
                <ActivityFeedNoteItem note={activity.payload as CrmNoteDTO} />
            );
        case ActivityFeedModelType.ORDER:
        case ActivityFeedModelType.QUOTE:
            return (
                <ActivityFeedQuoteOrderItem
                    payload={activity.payload as QuoteOrderPayload}
                    modelType={activity.model_type}
                />
            );
        case ActivityFeedModelType.INSIGHT_OUTCOME:
            return <ActivityFeedInsightOutcomeItem payload={activity.payload as InsInsightOutcomeDTO} />;
        case ActivityFeedModelType.CUSTOMER_STATUS_CHANGE:
            return (
                <ActivityFeedCustomerStatusChangeItem
                    payload={activity.payload as CrmCustomerStatusChangeDTO}
                />
            );
    }
}
